import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { Checkbox } from "../Checkbox";

const listItemVariants = cva(
  "flex items-start gap-3 p-3 rounded-medium transition-colors cursor-pointer",
  {
    variants: {
      variant: {
        default: `hover:bg-action-fill-neutral-faded
          focus:bg-action-fill-neutral-faded
          focus:ring-2
          ring-action-outline-primary-faded-hover
          border border-transparent
          `,
        bordered:
          "border border-action-outline-primary-faded hover:bg-surface-fill-primary-subtle",
        primary: `hover:bg-action-fill-neutral-faded
          focus:bg-action-fill-neutral-faded 
          focus:ring-2 
          ring-action-outline-primary-faded-hover
          border border-transparent
          `,
        negative: `hover:bg-action-fill-negative-faded 
          focus:bg-action-fill-negative-faded 
          focus:ring-2 ring-action-outline-negative-faded-hover
          border border-transparent
          `,
      },
      isDisabled: {
        true: "cursor-not-allowed opacity-60",
        false: "",
      },
      isSelected: {
        true: "bg-action-fill-primary-faded border-action-outline-primary-faded",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      isDisabled: false,
      isSelected: false,
    },
  }
);

export interface ListItemProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "color"> {
  /**
   * Type of list item - determines if checkbox is shown
   */
  type?: "single" | "multiple";
  /**
   * Leading icon to display on the left
   */
  leadingIcon?: React.ReactNode;
  /**
   * Title text (required)
   */
  title: string;
  /**
   * Description text (optional)
   */
  description?: string;
  /**
   * Trailing icon to display on the right
   */
  trailingIcon?: React.ReactNode;
  /**
   * Whether to show the default chevron trailing icon
   */
  showChevron?: boolean;
  /**
   * Visual variant of the list item
   */
  variant?: "default" | "bordered" | "primary" | "negative";
  /**
   * Whether the list item is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether the list item is selected (for multiple type)
   */
  isSelected?: boolean;
  /**
   * Callback when the list item selection changes
   */
  onSelectionChange?: (selected: boolean) => void;
  /**
   * Size of the checkbox (when type is multiple)
   */
  checkboxSize?: "small" | "medium" | "large";
  /**
   * Custom class name for the container
   */
  containerClassName?: string;
  /**
   * Custom class name for the content area
   */
  contentClassName?: string;
}

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ListItem = React.forwardRef<HTMLDivElement, ListItemProps>(
  (
    {
      className,
      type = "single",
      leadingIcon,
      title,
      description,
      trailingIcon,
      showChevron = true,
      variant = "default",
      isDisabled = false,
      isSelected = false,
      onSelectionChange,
      checkboxSize = "small",
      containerClassName,
      contentClassName,
      onClick,
      ...props
    },
    ref
  ) => {
    const [internalSelected, setInternalSelected] = React.useState(isSelected);

    // Sync internal state with prop
    React.useEffect(() => {
      setInternalSelected(isSelected);
    }, [isSelected]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDisabled) return;

      if (type === "multiple") {
        const newSelected = !internalSelected;
        setInternalSelected(newSelected);
        onSelectionChange?.(newSelected);
      }

      onClick?.(e);
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (isDisabled) return;

      const newSelected = e.target.checked;
      setInternalSelected(newSelected);
      onSelectionChange?.(newSelected);
    };

    return (
      <div
        ref={ref}
        className={cn(
          listItemVariants({
            variant,
            isDisabled,
            isSelected: type === "multiple" ? internalSelected : false,
          }),
          containerClassName
        )}
        onClick={handleClick}
        role={type === "multiple" ? "checkbox" : "button"}
        aria-checked={type === "multiple" ? internalSelected : undefined}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
        {...props}
      >
        {/* Checkbox for multiple type */}
        {type === "multiple" && (
          <Checkbox
            checked={internalSelected}
            onChange={handleCheckboxChange}
            isDisabled={isDisabled}
            size={checkboxSize}
            className="shrink-0 mt-0.5"
          />
        )}

        {/* Leading Icon */}
        {leadingIcon && (
          <div
            className={cn(
              `shrink-0 flex items-center justify-center mt-0.5`,
              variant === "primary"
                ? "text-action-ink-primary-normal"
                : variant === "negative"
                ? "text-action-ink-negative-normal"
                : "text-action-ink-neutral-subtle",
              isDisabled && "text-surface-ink-neutral-disabled"
            )}
          >
            {leadingIcon}
          </div>
        )}

        {/* Content */}
        <div
          className={cn(
            "flex-1 min-w-0 flex flex-col justify-center",
            contentClassName
          )}
        >
          <div
            className={cn(
              "text-body-medium-regular truncate",
              variant === "primary"
                ? "text-action-ink-primary-normal"
                : variant === "negative"
                ? "text-action-ink-negative-normal"
                : "text-action-ink-neutral-normal",
              isDisabled && "text-surface-ink-neutral-disabled"
            )}
          >
            {title}
          </div>
          {description && (
            <div
              className={cn(
                "text-body-small-regular text-surface-ink-neutral-muted mt-0.5 line-clamp-2",
                isDisabled && "text-surface-ink-neutral-disabled"
              )}
            >
              {description}
            </div>
          )}
        </div>

        {/* Trailing Icon */}
        {(trailingIcon || showChevron) && (
          <div className="shrink-0 self-center text-action-ink-neutral-subtle">
            {trailingIcon || <ChevronRightIcon />}
          </div>
        )}
      </div>
    );
  }
);

ListItem.displayName = "ListItem";

export { ListItem, listItemVariants };
